import React, { useState, useEffect } from "react";
import Header from "../../Header/Header";
import CustomTable from "../../../components/common/CustomTable";
import CustomButton from "../../../components/common/CustomButton";
import CustomModel from "../../../components/common/CustomModel";
import CustomHeading from "../../../components/common/CustomHeading";
import CustomSearch from "../../../components/common/CustomSearch";
import { ToastContainer } from "react-toastify";
import { EditIcon } from "lucide-react";
import ShiftTimeForm from "./ShiftTimeForm";
import shiftTimeService from "../../Services/shiftTimeService";
import { showErrorToast, showSuccessToast } from "../../../components/Constant/Notification";

const ShiftTimePage = () => {
  const [isShiftTimeOpen, setIsShiftTimeOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [nextId, setNextId] = useState(null);
  const [previousId, setPreviousId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Get current user data
  const userData = JSON.parse(localStorage.getItem("userData"));
  const currentUserId = userData?.user?.uid;

  // Convert time to 12-hour format for display
  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const columns = [
    { 
      key: "weekdayShiftTime", 
      title: "Weekday Shift Time",
      render: (text, record) => (
        <div className="text-center">
          <div className="font-medium">
          {text}
          </div>
        </div>
      ),
    },
    { 
      key: "weekendShiftTime", 
      title: "Weekend Shift Time",
      render: (text, record) => (
        <div className="text-center">
          <div className="font-medium">
          {text}
          </div>
        </div>
      ),
    },
    { 
      key: "membersCount", 
      title: " Members",
      render: (text, record) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {text}
          </span>
        </div>
      ),
    },
  
    {
      key: "actions",
      title: "Actions",
      render: (text, record) => (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => handleEdit(record)}
            className="text-blue-500 hover:text-blue-700 p-1 rounded transition-colors"
            title="Edit Shift Time"
          >
            <EditIcon size={16} />
          </button>
        </div>
      ),
    },
  ];

  // Fetch shift times from API
  const fetchShiftTimes = async (page = 1, search = "", reset = false) => {
    try {
      setLoading(true);
      
      const body = {
        page: page,
        limit: pageLimit,
        searchKey: search.trim(), // Fixed to match API expectation
        ...(nextId && !reset && page > currentPage ? { next: nextId } : {}),
        ...(previousId && !reset && page < currentPage ? { prev: previousId } : {}),
      };

      const response = await shiftTimeService.fetchShiftTimes(body);
      
      if (response.status === 200) {
        const { shifts, totalCount, next, prev } = response.data;
        
        // Transform data to ensure consistent structure
        const transformedShifts = shifts?.map(shift => ({
          ...shift,
          weekdayShiftTime: shift.weekdayLoginRailwayTime && shift.weekdayLogoutRailwayTime 
            ? `${convertTo12Hour(shift.weekdayLoginRailwayTime)} - ${convertTo12Hour(shift.weekdayLogoutRailwayTime)}`
            : 'Not Set',
          weekendShiftTime: shift.weekendLoginRailwayTime && shift.weekendLogoutRailwayTime
            ? `${convertTo12Hour(shift.weekendLoginRailwayTime)} - ${convertTo12Hour(shift.weekendLogoutRailwayTime)}`
            : 'Not Set',
          teams: shift.teams || [],
          members: shift.members || shift.users || []
        })) || [];
        
        setData(transformedShifts);
        setTotalCount(totalCount || 0);
        setNextId(next || null);
        setPreviousId(prev || null);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching shift times:", error);
      showErrorToast(error.response?.data?.message || "Failed to fetch shift times");
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

 
  useEffect(() => {
    fetchShiftTimes(1, searchQuery, true);
  }, [pageLimit]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {

    e.preventDefault();
    setCurrentPage(1);
    setNextId(null);
    setPreviousId(null);
    fetchShiftTimes(1, searchQuery, true);
  };

  const handlePageSize = (size) => {
    setPageLimit(size);
    setCurrentPage(1);
    setNextId(null);
    setPreviousId(null);
  };

  const handlePageChange = (page) => {
    fetchShiftTimes(page, searchQuery, false);
  };

  const handleEdit = (record) => {
    setEditingShift(record);
    setIsEditing(true);
    setIsShiftTimeOpen(true);
  };

  const handleAddShift = () => {
    setEditingShift(null);
    setIsEditing(false);
    setIsShiftTimeOpen(true);
  };

  const handleModalClose = () => {
    setIsShiftTimeOpen(false);
    setIsEditing(false);
    setEditingShift(null);
   
    fetchShiftTimes(currentPage, searchQuery, false);
  };

  return (
    <div>
      <Header />
      <ToastContainer />
      <div className="px-6 pt-28 pb-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <CustomHeading title="Shift Time Management" isIcon={true} textColor="primary" />
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
            <div className="w-full">
              <CustomSearch
                placeholder="Search users"
                handleSearchChange={handleSearchChange}
                searchTerm={searchQuery}
                handleSearchSubmit={handleSearchSubmit}
              />
            </div>
            <CustomButton 
              label="Add Shift Time" 
              onClick={handleAddShift}
             className={"text-nowrap"}
            />
          </div>
        </div>

       
        <div className="bg-white rounded-lg shadow-sm">
          <CustomTable
            data={data}
            columns={columns}
            isPagination={true}
            pageSize={pageLimit}
            currentPage={currentPage}
            nextId={nextId}
            prevId={previousId}
            hasMore={!!nextId}
            loading={loading}
            onPageSizeChange={handlePageSize}
            onPageChange={handlePageChange}
            emptyMessage="No shift times found. Create your first shift time to get started."
          />
        </div>
      </div>

      <CustomModel
        title={isEditing ? "Edit Shift Time" : "Create Shift Time"}
        isOpen={isShiftTimeOpen}
        width="w-[800px]"
        isClose={handleModalClose}
        content={
          <ShiftTimeForm
            isEditing={isEditing}
            editingShift={editingShift}
            setData={setData}
            data={data}
            setIsShiftTimeOpen={handleModalClose}
          />
        }
        onConfirmText="Save Shift"
        showFooter={false}
      />
    </div>
  );
};

export default ShiftTimePage;